import { useEffect, useMemo, useState } from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";
import Grid from "@mui/material/Grid";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import PageLayout from "examples/LayoutContainers/PageLayout";
import { IoLockClosedOutline, IoDocumentTextOutline, IoShieldCheckmarkOutline, IoMailOutline } from "react-icons/io5";

const API_BASE_URL = process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1";
const APP_NAME = process.env.REACT_APP_NAME || "LedgerHub";

const LEGAL_PAGES = {
  "/legal/privacy-policy": {
    title: "Privacy Policy",
    tagline: "How we collect, use and protect your data",
    endpoint: "/legal/privacy-policy",
    type: "text",
    Icon: IoLockClosedOutline,
  },
  "/legal/terms-and-conditions": {
    title: "Terms & Conditions",
    tagline: "The rules that govern your use of the Service",
    endpoint: "/legal/terms-and-conditions",
    type: "text",
    Icon: IoDocumentTextOutline,
  },
  "/legal/security-contact": {
    title: "Security Contact",
    tagline: "Report a vulnerability or security incident",
    endpoint: "/legal/security-contact",
    type: "contact",
    Icon: IoShieldCheckmarkOutline,
  },
};

const NAV_LINKS = [
  { to: "/legal/privacy-policy", label: "Privacy Policy" },
  { to: "/legal/terms-and-conditions", label: "Terms & Conditions" },
  { to: "/legal/security-contact", label: "Security Contact" },
];

// Card shell shared by all three legal pages so they present a single,
// consistent, professional look across Privacy Policy, Terms & Conditions
// and Security Contact.
function LegalCard({ children }) {
  return (
    <VuiBox
      p={{ xs: 3, md: 5 }}
      borderRadius="lg"
      role="status"
      aria-live="polite"
      sx={{
        background: "rgba(8, 15, 30, 0.55)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(8px)",
        color: "white",
      }}
    >
      {children}
    </VuiBox>
  );
}

function TextDocument({ content }) {
  // The API response separates sections with a blank line, but a heading's
  // own body paragraph is only separated from the heading by a single
  // newline (not a blank one) — so each block below may contain a numbered
  // heading followed directly by its paragraph. Split those apart so only
  // the heading renders bold, not the whole section.
  const blocks = useMemo(
    () =>
      content
        .split(/\n\n+/)
        .map((block) => block.trim())
        .filter(Boolean),
    [content]
  );

  return blocks.map((block, index) => {
    const lines = block.split("\n");
    const firstLine = lines[0];
    const rest = lines.slice(1).join("\n").trim();

    // Block 0 is the document title, optionally followed by "Effective
    // date / Last updated / Version" meta lines on the next lines.
    if (index === 0) {
      return (
        <VuiBox key={index} mb={3} pb={2} sx={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            {firstLine}
          </VuiTypography>
          {rest && (
            <VuiTypography
              variant="caption"
              color="text"
              sx={{ whiteSpace: "pre-line", display: "block", mt: 1, lineHeight: 1.6 }}
            >
              {rest}
            </VuiTypography>
          )}
        </VuiBox>
      );
    }

    const isNumberedHeading = /^\d+\.\s/.test(firstLine);
    const isLabelHeading = !isNumberedHeading && /^[A-Za-z][A-Za-z\s&]{2,40}:$/.test(firstLine);

    // Heading with its own paragraph merged into the same block — render
    // the heading in bold and the paragraph underneath at normal weight.
    if ((isNumberedHeading || isLabelHeading) && rest) {
      return (
        <VuiBox key={index} mb={3} mt={index === 1 ? 0 : 1}>
          <VuiTypography variant="h5" color="white" fontWeight="bold" sx={{ mb: 1 }}>
            {firstLine}
          </VuiTypography>
          <VuiTypography
            variant="body1"
            color="white"
            fontWeight="regular"
            sx={{ whiteSpace: "pre-line", textAlign: "left", lineHeight: 1.8, fontSize: "1rem" }}
          >
            {rest}
          </VuiTypography>
        </VuiBox>
      );
    }

    // A standalone heading with no body, or a plain paragraph.
    const isHeadingOnly = (isNumberedHeading || isLabelHeading) && !rest;
    return (
      <VuiTypography
        key={index}
        variant={isHeadingOnly ? "h5" : "body1"}
        color="white"
        fontWeight={isHeadingOnly ? "bold" : "regular"}
        sx={{
          whiteSpace: "pre-line",
          textAlign: "left",
          lineHeight: 1.8,
          mb: index === blocks.length - 1 ? 0 : isHeadingOnly ? 1 : 3,
          mt: isHeadingOnly ? 1 : 0,
          fontSize: isHeadingOnly ? undefined : "1rem",
        }}
      >
        {block}
      </VuiTypography>
    );
  });
}

function SecurityContactDocument({ data }) {
  const email = data?.contactEmail || "admin@ledgerhubltd.co.uk";
  const contactUrl = data?.contactUrl;
  const message =
    data?.message || "Please report HMRC-related security incidents or data-breach concerns immediately.";

  return (
    <VuiBox textAlign="left">
      <VuiBox display="flex" alignItems="center" gap={1.5} mb={2}>
        <IoShieldCheckmarkOutline size={28} color="#4caf50" />
        <VuiTypography variant="h5" color="white" fontWeight="bold">
          Reporting a security issue
        </VuiTypography>
      </VuiBox>
      <VuiTypography variant="body1" color="white" sx={{ lineHeight: 1.8, mb: 3, fontSize: "1rem" }}>
        {message} We take all reports seriously and investigate every submission promptly. If you believe you have
        found a vulnerability, a data-breach concern, or suspicious activity related to {APP_NAME}, please contact us
        using the details below rather than disclosing it publicly.
      </VuiTypography>

      <VuiBox
        p={3}
        borderRadius="lg"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        sx={{ background: "rgba(76, 175, 80, 0.12)", border: "1px solid rgba(76, 175, 80, 0.35)" }}
      >
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <IoMailOutline size={22} color="#4caf50" />
          <VuiBox>
            <VuiTypography variant="caption" color="text" textTransform="uppercase" fontWeight="bold">
              Security &amp; Data Protection Contact
            </VuiTypography>
            <VuiTypography variant="h6" color="white" fontWeight="bold">
              {email}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
        <VuiButton color="success" variant="gradient" component="a" href={`mailto:${email}`}>
          Email Us Now
        </VuiButton>
      </VuiBox>

      {contactUrl && (
        <VuiTypography variant="body2" color="text" mt={2}>
          Additional support:{" "}
          <a href={contactUrl} target="_blank" rel="noreferrer" style={{ color: "#8bc6ff" }}>
            {contactUrl}
          </a>
        </VuiTypography>
      )}

      <VuiBox mt={4} pt={3} sx={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
        <VuiTypography variant="button" color="white" fontWeight="bold">
          What happens next?
        </VuiTypography>
        <VuiTypography variant="body2" color="text" mt={1} sx={{ lineHeight: 1.8 }}>
          Confirmed incidents are triaged immediately, escalated to our Data Protection contact, and — where
          required — reported to HMRC and the Information Commissioner's Office (ICO) within 72 hours, in line with
          our documented breach-notification process.
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );
}

function LegalPage() {
  const location = useLocation();
  const page = useMemo(() => LEGAL_PAGES[location.pathname] || LEGAL_PAGES["/legal/privacy-policy"], [location.pathname]);
  const [content, setContent] = useState("");
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setContent("");
    setContactData(null);

    fetch(`${API_BASE_URL}${page.endpoint}`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Unable to load the requested legal page.");
        if (page.type === "contact") {
          const json = await response.json();
          if (!cancelled) setContactData(json);
        } else {
          const text = await response.text();
          if (!cancelled) setContent(text);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Unable to load the requested legal page.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page.endpoint, page.type]);

  const PageIcon = page.Icon;

  return (
    <PageLayout>
      <DefaultNavbar
        action={{
          type: "internal",
          route: "/authentication/sign-in",
          label: "Sign In",
        }}
        light
      />
      <VuiBox minHeight="70vh" px={3} pt={{ xs: 18, lg: 16 }} pb={8} maxWidth="900px" mx="auto">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <VuiBox mb={4} display="flex" alignItems="center" gap={1.5}>
              <VuiBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  flexShrink: 0,
                }}
              >
                <PageIcon size={26} color="#8bc6ff" />
              </VuiBox>
              <VuiBox>
                <VuiTypography variant="h2" color="white" fontWeight="bold">
                  {page.title}
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular">
                  {APP_NAME} — UK Accounting Practice Platform · {page.tagline}
                </VuiTypography>
              </VuiBox>
            </VuiBox>

            <LegalCard>
              {loading ? (
                <VuiTypography variant="body1" color="white">
                  Loading...
                </VuiTypography>
              ) : error ? (
                <VuiTypography variant="body1" color="white">
                  {error}
                </VuiTypography>
              ) : page.type === "contact" ? (
                <SecurityContactDocument data={contactData} />
              ) : (
                <TextDocument content={content} />
              )}
            </LegalCard>

            <VuiBox mt={3} display="flex" gap={2} flexWrap="wrap">
              {NAV_LINKS.map((link) => (
                <VuiTypography
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  variant="button"
                  fontWeight="medium"
                  sx={{
                    color: location.pathname === link.to ? "#ffffff" : "#8bc6ff",
                    textDecoration: location.pathname === link.to ? "none" : "underline",
                    opacity: location.pathname === link.to ? 1 : 0.9,
                  }}
                >
                  {link.label}
                </VuiTypography>
              ))}
            </VuiBox>
          </Grid>
        </Grid>
      </VuiBox>
    </PageLayout>
  );
}

export default LegalPage;

